import type { GridFilterModel, GridPaginationModel, GridSortModel } from '@mui/x-data-grid-pro';
import type { GridInitialStatePro } from '@mui/x-data-grid-pro/models/gridStatePro';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  deserializeFilterModel,
  deserializePagination,
  deserializeSortModel,
  serializeFilterModel,
  serializePagination,
  serializeSortModel,
} from '../../utils/serializers';
import { useDataGridInitialState } from '../useDataGridInitialState/useDataGridInitialState';

export const useDataGridState = (
  disableSearchParamSync: boolean,
  pageSizeOptions: readonly (number | { value: number; label: string })[],
  initialState: GridInitialStatePro | undefined,
  { pagination, initialPageSize }: { pagination: boolean | undefined; initialPageSize: number }
) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel | undefined>(
    deserializePagination(searchParams, initialPageSize)
  );
  const [sortModel, setSortModel] = useState<GridSortModel | undefined>(
    deserializeSortModel(searchParams)
  );
  const [filterModel, setFilterModel] = useState<GridFilterModel | undefined>(
    deserializeFilterModel(searchParams)
  );

  // extract initial state from URL and props
  const _computedInitialState = useDataGridInitialState(initialState, {
    pagination,
    initialPageSize,
  });

  // update search params based on state
  const updateSearchParams = useCallback(
    (
      paginationModel: GridPaginationModel | undefined,
      sortModel: GridSortModel | undefined,
      filterModel: GridFilterModel | undefined
    ) => {
      // serialize pagination, sort and filter models
      const serializedPaginationModel = serializePagination(paginationModel);
      const serializedSortModel = serializeSortModel(sortModel);
      const serializedFilterModel = serializeFilterModel(filterModel);

      setSearchParams((_prevParams) => {
        const params = new URLSearchParams(_prevParams);
        pagination &&
          serializedPaginationModel &&
          params.set('page', (serializedPaginationModel.page + 1).toString());
        pagination &&
          serializedPaginationModel &&
          params.set('pageSize', serializedPaginationModel.pageSize.toString());
        serializedSortModel && params.set('sort', serializedSortModel);
        serializedFilterModel?.filter && params.set('filter', serializedFilterModel.filter);
        serializedFilterModel?.filter &&
          params.set('logicOperator', serializedFilterModel.logicOperator || 'and');
        serializedFilterModel?.quickFilter &&
          params.set('quickFilter', serializedFilterModel.quickFilter);
        serializedFilterModel?.quickFilter &&
          params.set(
            'quickFilterLogicOperator',
            serializedFilterModel.quickFilterLogicOperator || 'and'
          );

        return params;
      });
    },
    [pagination, setSearchParams]
  );

  // check if the pageSize is available in pageSizeOptions
  const isPageSizeAvailable = useMemo(
    () => !!pageSizeOptions.find((option) => option === paginationModel?.pageSize),
    [pageSizeOptions, paginationModel?.pageSize]
  );

  // get the first pageSizeOption value
  const firstPageSizeOptionValue =
    typeof pageSizeOptions[0] === 'number' ? pageSizeOptions[0] : pageSizeOptions[0].value;

  // if check if the initialPageSize is not in pageSizeOptions
  const isInitialPageSizeInOptions = pageSizeOptions.find((option) =>
    typeof option === 'number' ? option === initialPageSize : option.value === initialPageSize
  );

  // sync search params with state, update pageSize if pageSize not in pageSizeOptions
  useEffect(() => {
    if (pagination && paginationModel?.pageSize && !isPageSizeAvailable) {
      // set pageSize to initialPageSize if it is in pageSizeOptions, otherwise set it to the first pageSizeOption value
      const _pageSize = isInitialPageSizeInOptions ? initialPageSize : firstPageSizeOptionValue;

      // if search param sync is not disabled, update search params
      !disableSearchParamSync &&
        updateSearchParams(
          {
            page: paginationModel.page,
            pageSize: _pageSize,
          },
          sortModel,
          filterModel
        );

      // update paginationModel state
      setPaginationModel((prev) => {
        return {
          page: prev?.page || 0,
          pageSize: _pageSize,
        };
      });
    } else {
      // if search param sync is not disabled, update search params
      !disableSearchParamSync && updateSearchParams(paginationModel, sortModel, filterModel);
    }
  }, [
    disableSearchParamSync,
    filterModel,
    firstPageSizeOptionValue,
    initialPageSize,
    isInitialPageSizeInOptions,
    isPageSizeAvailable,
    pagination,
    paginationModel,
    sortModel,
    updateSearchParams,
  ]);

  // handle local DataGrid state updates if pagination model changes
  const handlePaginationModelChange = (paginationModel: GridPaginationModel) => {
    setPaginationModel(paginationModel);
  };

  // handle local DataGrid state updates if sort model changes
  const handleSortModelChange = (sortModel: GridSortModel) => {
    setSortModel(sortModel);
  };

  // handle local DataGrid state updates if filter model changes
  const handleFilterModelChange = (filterModel: GridFilterModel) => {
    setFilterModel(filterModel);
  };

  return {
    initialState: _computedInitialState,
    handlePaginationModelChange,
    handleSortModelChange,
    handleFilterModelChange,
    paginationModel: pagination ? paginationModel : undefined,
    sortModel,
    filterModel,
  };
};
